/**
 * Экран «Активная тренировка» (раздел 8, пункт 2) — главный экран продукта.
 *
 * Всё здесь подчинено продуктовому принципу №2 (раздел 2): человек стоит в
 * зале между подходами, руки заняты, времени нет. Запись подхода обязана
 * укладываться в два касания. Отсюда три решения, которые выглядят как
 * ограничения, но ими не являются:
 *
 *   1. Клиент не считает ни одного числа. Вес посчитал и округлил под железо
 *      движок — у каждого подхода свой (`target_weight`), а `suggested_weight`
 *      это вес блока целиком; пересчитывать их здесь запрещено —
 *      принцип №1: цифры приходят с сервера. Но ПРАВИТЬ вес можно, и это не
 *      противоречие: `workout_set` — это ФАКТ, а не план. Стойка занята,
 *      блины разобрали, сил не хватило — человек сделал 90 вместо 100, и в
 *      базу обязано лечь 90. Записанные 100 отравят e1RM (5.3), прогноз
 *      (5.5) и автозагрузку по RPE: движок решит, что человек сильнее, чем
 *      он есть, и в следующий раз поставит вес, который тот не поднимет.
 *      План остаётся за движком, факт — за человеком.
 *   2. Вес, повторы и RPE предзаполнены плановыми значениями. Поэтому
 *      «сделал как написано» — это ОДИН тап по большой кнопке; правка любого
 *      из трёх — плюс один тап по шагу Stepper, итого два.
 *   3. Ни одного поля с клавиатурой. В вебвью Telegram на iOS она прыгает и
 *      перекрывает пол-экрана (раздел 13), а между подходами это фатально.
 *
 * Источник правды для экрана — локальное состояние `done`, а не ответ сервера.
 * Подход уходит в очередь синхронизации (`offline/queue`) и может пролежать
 * там до конца тренировки: в подвальном зале связи нет. Ждать сервер, чтобы
 * нарисовать галочку, значит потерять зал.
 *
 * Записанный подход можно ИСПРАВИТЬ здесь же: тап по его строке переводит те
 * же три степпера в режим правки. Промах по кнопке записи и «не тот вес» —
 * вещи, которые замечают сразу, и отправлять человека за этим в «Историю»
 * посреди тренировки значит, что ошибка останется в базе. Подход правится там,
 * где лежит: в очереди — без связи, на сервере — через `PATCH /sets/{id}`.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { NetworkError, api } from '../api/client'
import { BAR_CHOICES } from '../api/types'
import type {
  Today,
  ExerciseBlock,
  Flag,
  PainFlag,
  PlannedSet,
  RestTimer,
  Session,
  SetInput,
  WorkoutSet,
} from '../api/types'
import { useExercises } from '../api/exercises'
import { haptic } from '../auth/telegram'
import { ExerciseHead } from '../components/ExerciseHead'
import { GoalMark } from '../components/GoalMark'
import { ExercisePicker } from '../components/ExercisePicker'
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
} from '../components/icons'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Flags,
  Loader,
  Stepper,
  formatWeight,
} from '../components/ui'
import { useAsync } from '../hooks'
import { CACHE_TODAY, queuedItems, readCache, saveCache } from '../offline/db'
import {
  amendQueued,
  newUuid,
  recordSet,
  removeQueued,
  type SetChanges,
} from '../offline/queue'

/**
 * Отдых по умолчанию (раздел 7): базовые движения дольше, изоляция короче.
 * Числа не про нагрузку — сервер всё равно возвращает свой `seconds` в ответе.
 */
const REST_COMPOUND_SEC = 180
const REST_ISOLATION_SEC = 90

/**
 * Шаги правки веса: крупным доезжают, мелким попадают.
 *
 * 2.5 кг — пара самых ходовых мелких блинов (2×1.25), обычный шаг штанги.
 * Раньше он был единственным, и это оказалось ошибкой: факт в зале ему не
 * кратен. Гантели идут через килограмм, у стеков тренажёров шаг свой (а то и
 * в фунтах), бывают блины по 0.5 и 0.25. До своего веса было не добраться, и
 * человек записывал ближайший — то есть неправду, из которой потом считаются
 * e1RM (5.3), прогноз (5.5) и автозагрузка. Мелкий шаг 0.25 кг закрывает любой
 * реальный вес; от крупного до нужного — не больше пяти нажатий.
 *
 * Это шаги ВВОДА ФАКТА, а не расчёт нагрузки (принцип №1): что назначить,
 * по-прежнему решает движок и округляет под своё железо.
 */
const WEIGHT_STEP_KG = 2.5
const WEIGHT_FINE_STEP_KG = 0.25

/** Потолок степпера веса: выше в любительском зале не грузят. */
const WEIGHT_MAX_KG = 500

/**
 * Потолок степпера повторов. Обычный подход — до 50; AMRAP («до отказа»,
 * раздел 5) на лёгком весе легко уходит за 50, и упереться в границу там
 * значит записать неправду.
 */
const REPS_MAX = 50
const REPS_MAX_AMRAP = 100

/**
 * Нижняя граница RPE. Шкала начинается с 5 (ниже — не рабочий подход), и
 * сервер вне 1..10 запись не принимает: подход с RPE 0 уехал бы в очередь и
 * молча слёг бы там сбоем. Значение подставляется только там, где плана нет
 * вовсе, — своим числом оно ничего не назначает, человек правит его тапом.
 */
const RPE_MIN = 5

/** Записанный факт — то, что уже лежит в очереди синхронизации или на сервере. */
interface DoneSet {
  weight: number
  reps: number
  rpe: number
  /** `client_uuid` записи: по нему подход ищется в очереди и на сервере. */
  uuid?: string
  /** Номер записи на сервере — известен, если подход приехал оттуда. */
  id?: number
}

/**
 * Правка уже записанного подхода.
 *
 * Отдельного редактора нет: в режим правки переходят те же три степпера, что
 * записывают подход. Вторая пара полей на экране — это вопрос «а какие из них
 * настоящие?» в момент, когда руки заняты.
 */
interface Editing {
  exerciseId: number
  setNo: number
  weight: number
  reps: number
  rpe: number
}

/** exercise_id -> set_no -> факт. */
type DoneMap = Record<number, Record<number, DoneSet> | undefined>

/**
 * Правка текущего подхода. Ключ привязывает её к конкретному подходу, поэтому
 * при переходе к следующему значения сами возвращаются к плановым — человеку
 * не приходится вспоминать, что он там накрутил в прошлый раз.
 */
interface Draft {
  key: string
  weight: number
  reps: number
  rpe: number
}

interface RestState {
  /** Момент конца отдыха в миллисекундах устройства. */
  deadline: number
  seconds: number
  /** `null` — сервер ещё не ответил, про доставку сказать пока нечего. */
  delivery: RestTimer['delivery'] | null
  note: string
  /**
   * Таймер не встал: напоминания не будет, и врать об этом нельзя.
   *
   * Причина разделена не ради точности формулировки, а ради совета: «нет
   * связи» пройдёт само, как только появится сеть, а отказ сервера сам не
   * пройдёт. Смешать их значит отправить человека чинить не то.
   */
  failed: 'offline' | 'server' | null
}

/** Обратный отсчёт в виде «2:05». Это форматирование, а не расчёт нагрузки. */
function clock(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * Момент, когда отдых кончится.
 *
 * Сервер отдаёт `fires_at` в часовом поясе пользователя — то есть в поясе
 * этого же телефона, поэтому строка разбирается корректно и со смещением, и
 * без него. Если разобрать не вышло вовсе, отсчитываем от «сейчас»: на экране
 * лучше приблизительный таймер, чем NaN.
 */
function deadlineOf(timer: RestTimer): number {
  const parsed = Date.parse(timer.fires_at)
  return Number.isNaN(parsed) ? Date.now() + timer.seconds * 1000 : parsed
}

/**
 * Вес для показа.
 *
 * Ноль и `null` — РАЗНЫЕ вещи, и путать их нельзя. Ноль на движении без
 * отягощения (подтягивания, планка) — это «свой вес», и «0 кг» там выглядит
 * как сбой. А `null` значит, что движок веса не назначил вовсе: на
 * калибровочной неделе (раздел 5.2) истории ещё нет, и назначать не из чего.
 * Написать «свой вес» под штангой в этом случае — соврать человеку про план
 * (принцип честности); остальные экраны ставят здесь прочерк, и здесь он же.
 */
function weightLabel(kg: number | null, ownWeight: boolean): string {
  if (kg === null) return ownWeight ? 'свой вес' : '—'
  if (kg === 0) return ownWeight ? 'свой вес' : '0 кг'
  return `${formatWeight(kg)} кг`
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

const RING_SIZE = 46
const RING_STROKE = 5
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2
const RING_LENGTH = 2 * Math.PI * RING_RADIUS

/**
 * Кольцо отдыха: дуга убывает вместе с отсчётом.
 *
 * Доля — это перевод секунд в длину дуги, то же форматирование, что и «2:05»
 * рядом; про нагрузку здесь ничего не считается (принцип №1).
 */
function RestRing({ left, total }: { left: number; total: number }) {
  const share = total > 0 ? Math.min(1, Math.max(0, left / total)) : 0
  return (
    <svg
      className="wk-ring"
      width={RING_SIZE}
      height={RING_SIZE}
      viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      aria-hidden="true"
    >
      <circle
        className="wk-ring-track"
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RING_RADIUS}
        strokeWidth={RING_STROKE}
      />
      {share > 0 && (
        <circle
          className="wk-ring-arc"
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - share)}
        />
      )}
    </svg>
  )
}

/** «в 18:42» для сегодняшней отметки, «8 сентября» для более старой. */
function formatStamp(ms: number): string {
  const saved = new Date(ms)
  const sameDay = saved.toDateString() === new Date().toDateString()
  return sameDay
    ? `в ${saved.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
    : saved.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}


export default function Workout() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  // Отметка времени, если план приехал из кеша, а не с сервера.
  const [cachedAt, setCachedAt] = useState<number | null>(null)

  /**
   * План тренировки с запасным путём на случай зала без связи.
   *
   * Раздел 3 требует кеш в IndexedDB, раздел 13 называет риск «логирование
   * медленнее бумажки». Без этого запаса очередь подходов бесполезна при
   * первом входе: записывать не по чему, план взять неоткуда.
   *
   * Порядок источников: сервер, затем сохранённый план этой тренировки,
   * затем ответ экрана «Сегодня» — его человек обычно открывает дома, и
   * в нём лежит та же сессия с блоками.
   */
  const state = useAsync<Session>(
    async () => {
      if (!sessionId) throw new Error('Не указано, какую тренировку открыть')
      const key = `session:${sessionId}`
      try {
        const fresh = await api.get<Session>(`/sessions/${sessionId}`)
        setCachedAt(null)
        void saveCache(key, fresh)
        return fresh
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error

        const saved = await readCache<Session>(key)
        if (saved) {
          setCachedAt(saved.savedAt)
          return saved.value
        }

        const today = await readCache<Today>(CACHE_TODAY)
        if (today?.value.session && String(today.value.session.id) === sessionId) {
          setCachedAt(today.savedAt)
          return today.value.session
        }
        throw error
      }
    },
    [sessionId],
  )

  const [index, setIndex] = useState(0)
  const [done, setDone] = useState<DoneMap>({})
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saveError, setSaveError] = useState('')

  const [editing, setEditing] = useState<Editing | null>(null)
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState('')

  const [rest, setRest] = useState<RestState | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [restHint, setRestHint] = useState('')
  const buzzed = useRef(false)

  const [painFor, setPainFor] = useState<number | null>(null)
  const [severity, setSeverity] = useState(3)
  const [painNotes, setPainNotes] = useState<Record<number, string | undefined>>({})
  const [painError, setPainError] = useState('')
  const [painSending, setPainSending] = useState(false)

  const [finishError, setFinishError] = useState('')
  const [finishing, setFinishing] = useState(false)

  // Добавление движения по ходу тренировки. Сделал в кроссовере то, чего в
  // плане нет, — дописать это нужно здесь и сейчас, а не вспоминать вечером.
  const catalog = useExercises()
  const [adding, setAdding] = useState(false)
  /**
   * Предупреждения сервера о последнем добавлении — например, что группа за
   * неделю вышла выше коридора. Панель после добавления закрывается, поэтому
   * они живут отдельно от неё и видны до следующего открытия.
   */
  const [addFlags, setAddFlags] = useState<Flag[]>([])
  const [addError, setAddError] = useState('')
  const [addBusy, setAddBusy] = useState(false)

  // Выбор грифа. Свёрнут по умолчанию: между подходами на экране важны три
  // цифры подхода, а гриф выставляют один раз и надолго.
  const [barOpen, setBarOpen] = useState(false)
  const [barError, setBarError] = useState('')
  const [barBusy, setBarBusy] = useState(false)

  // Подсказка по технике. Открыта или нет — состояние экрана, а не движения:
  // при переходе к следующему упражнению закрывается (см. goTo).
  const [techniqueOpen, setTechniqueOpen] = useState(false)

  const session = state.data
  const blocks = session?.blocks ?? []

  const safeIndex = blocks.length ? Math.min(index, blocks.length - 1) : 0
  const block = blocks[safeIndex]

  /** Движение без внешнего отягощения: у него отсутствие веса — это норма. */
  const ownWeight = block?.exercise.equipment === 'bodyweight'

  /**
   * Вес пустого грифа — или null, если движение не штанговое.
   *
   * Приходит с сервера и НЕ вычисляется здесь: это `min_weight` движения под
   * своим именем, то есть свойство железа, которое клиенту знать неоткуда.
   * С ним поле веса перестаёт требовать сложения в уме: человек накидывает
   * блины, а в `workout_set` уходит поднятый вес целиком.
   */
  const barWeight = block?.exercise.bar_weight ?? null

  /**
   * Вес конкретного планового подхода.
   *
   * Порядок важен: свой вес подхода важнее общего по упражнению. В процентных
   * программах (5/3/1, GZCLP) подходы идут рампой — 65 / 75 / 85 % и объёмная
   * часть на 50 %, у каждого свой `target_weight`, — а `suggested_weight`
   * там лишь ВЕРХНИЙ из них, сводка для карточки «Сегодня». Подставить его во
   * все подходы значит показать неправду на экране и, что хуже, записать в
   * `workout_set` вес, которого человек не поднимал: e1RM (5.3), прогноз (5.5)
   * и автозагрузка по RPE (5.4) считаются именно из этих фактов.
   *
   * `suggested_weight` остаётся запасным вариантом — он и нужен для подходов
   * без собственного веса (объёмная часть процентной программы) и для обычных
   * блоков движка, где у всех подходов вес один и тот же.
   */
  const setWeight = (s: PlannedSet | undefined): number | null =>
    s?.target_weight ?? block?.suggested_weight ?? null

  // Отсчёт тикает, только пока экран открыт, — и только он. Разбудить себя в
  // фоне веб на iOS не может (раздел 7), поэтому напоминание в срок присылает
  // бот; цифра на экране нужна ровно тому, кто на экран смотрит.
  useEffect(() => {
    if (!rest) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(timer)
  }, [rest])

  const left = rest ? Math.max(0, Math.ceil((rest.deadline - now) / 1000)) : 0

  // Вибрация в момент окончания отдыха: телефон обычно лежит рядом экраном
  // вверх, и это дешевле, чем следить за цифрами.
  useEffect(() => {
    if (rest && left === 0 && !buzzed.current) {
      buzzed.current = true
      haptic('heavy')
    }
  }, [rest, left])

  // Восстановление уже записанного.
  //
  // Экран открывают повторно чаще, чем кажется: вебвью Telegram выгружает
  // вкладку, пока телефон лежит в кармане между подходами, да и кнопка выхода
  // стоит прямо в шапке. Без восстановления возврат показывает тренировку
  // нетронутой, человек записывает те же подходы заново — и в `workout_set`
  // ложатся дубли: идемпотентность держится на `client_uuid`, а у повторной
  // записи он новый.
  //
  // Источников два, и нужны оба: сервер знает то, что уже уехало, очередь —
  // то, что ещё лежит на устройстве и в зале без связи является единственным
  // источником. Локально записанное всегда важнее: оно новее.
  const currentSessionId = session?.id
  useEffect(() => {
    if (currentSessionId === undefined) return undefined
    let cancelled = false

    const merge = (found: DoneMap): void => {
      if (cancelled) return
      setDone((prev) => {
        const next: DoneMap = { ...prev }
        for (const [exercise, sets] of Object.entries(found)) {
          const key = Number(exercise)
          next[key] = { ...sets, ...next[key] }
        }
        return next
      })
    }

    void queuedItems()
      .then((items) => {
        const found: DoneMap = {}
        for (const { uuid, payload } of items) {
          if (payload.session_id !== currentSessionId || payload.is_warmup) continue
          found[payload.exercise_id] = {
            ...found[payload.exercise_id],
            [payload.set_no]: {
              weight: payload.weight,
              reps: payload.reps,
              rpe: payload.rpe ?? 0,
              uuid,
            },
          }
        }
        merge(found)
      })
      .catch(() => undefined)

    void api
      .get<WorkoutSet[]>(`/sets?session_id=${currentSessionId}`)
      .then((rows) => {
        const found: DoneMap = {}
        for (const row of rows) {
          if (row.is_warmup) continue
          // Сервер отдаёт от свежего к старому. Если под одним номером лежат
          // две записи, на экране и в правке должна быть свежая.
          if (found[row.exercise_id]?.[row.set_no]) continue
          found[row.exercise_id] = {
            ...found[row.exercise_id],
            [row.set_no]: {
              weight: row.weight,
              reps: row.reps,
              rpe: row.rpe ?? 0,
              id: row.id,
              uuid: row.client_uuid ?? undefined,
            },
          }
        }
        merge(found)
      })
      // Связи нет — показываем то, что нашлось в очереди. Ошибку не рисуем:
      // очередь и есть источник правды для этого экрана.
      .catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [currentSessionId])

  // Отдых тоже переживает перезагрузку: отсчёт ведёт сервер (раздел 7),
  // поэтому после возврата спрашиваем у него, а не начинаем заново.
  // `prev ?? ...` — чтобы медленный ответ не затёр таймер уже нового подхода.
  useEffect(() => {
    let cancelled = false
    void api
      .get<RestTimer | null>('/rest-timer')
      .then((timer) => {
        if (cancelled || !timer) return
        const deadline = deadlineOf(timer)
        if (deadline <= Date.now()) return
        setNow(Date.now())
        setRest((prev) => {
          if (prev) return prev
          buzzed.current = false
          return {
            deadline,
            seconds: timer.seconds,
            delivery: timer.delivery,
            note: timer.note,
            failed: null,
          }
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const doneHere = block ? done[block.exercise.id] : undefined
  const currentSet = block?.sets.find((s) => !doneHere?.[s.set_no])

  /**
   * Сверхплановый подход.
   *
   * План закрыт, а человек сделал ещё один — это нормально и это факт, а факт
   * богаче плана (принцип №3). Номер берём следующим за МАКСИМАЛЬНЫМ уже
   * записанным, а не за последним плановым: подходы могли лечь не по порядку
   * (часть приехала с сервера, часть из очереди), а `set_no` обязан остаться
   * уникальным в пределах упражнения. Сервер такой номер принимает: он не
   * сверяет факт с планом.
   */
  const doneNumbers = Object.keys(doneHere ?? {}).map(Number)
  const lastDoneNo = doneNumbers.length ? Math.max(...doneNumbers) : 0
  const extraSetNo = lastDoneNo + 1
  const lastFact = doneHere?.[lastDoneNo]
  const lastPlanned = block ? block.sets[block.sets.length - 1] : undefined

  // Ключ правки. У сверхпланового подхода он свой, и после записи меняется —
  // значит, следующий сверхплановый снова начинается с умолчаний.
  const currentKey = block
    ? currentSet
      ? `${block.exercise.id}:${currentSet.set_no}`
      : `${block.exercise.id}:+${extraSetNo}`
    : ''

  /**
   * Умолчания. У планового подхода это то, что назначил движок: вес этого же
   * подхода, повторы — верх диапазона, RPE — плановый.
   *
   * У сверхпланового плана нет вовсе, поэтому повторяем последний записанный
   * факт. Это не расчёт (принцип №1), а копия уже сделанного: лишний подход
   * почти всегда делают тем же весом, что и предыдущий.
   *
   * Веса может не быть и у планового подхода — калибровочная неделя (5.2):
   * истории нет, назначать не из чего, и движок честно присылает `null`.
   * Тогда подставляем уже записанное: сначала факт этой тренировки, потом
   * вес прошлой сессии от сервера (он же показан строкой «в прошлый раз»).
   * Это снова копия чужого числа, а не расчёт, — и без неё второй
   * калибровочный подход пришлось бы набирать с нуля по 2.5 кг за тап.
   *
   * И третий случай: человек ПОПРАВИЛ вес в прошлом подходе. Если план у
   * прошлого и текущего подхода один (обычные «4×8 одним весом»), в поле
   * встаёт факт прошлого подхода, а не план: на штанге уже висит то, что он
   * поставил, и заставлять его накручивать ту же поправку в каждом из четырёх
   * подходов — верный способ получить в базе плановый вес вместо настоящего.
   * Там, где веса подходов разные (рампа 5/3/1), правило молчит: вес у каждого
   * подхода свой, и прошлый факт про следующий ничего не говорит.
   */
  const planWeight = setWeight(currentSet)
  const knownWeight = lastFact?.weight ?? block?.last_session_weight ?? null
  const lastDonePlanned = block?.sets.find((s) => s.set_no === lastDoneNo)
  const carriedWeight =
    currentSet && lastFact && lastDonePlanned && setWeight(lastDonePlanned) === planWeight
      ? lastFact.weight
      : null
  const defaultWeight = currentSet
    ? carriedWeight ?? planWeight ?? knownWeight ?? 0
    : lastFact?.weight ?? planWeight ?? knownWeight ?? 0
  /**
   * Повторы по умолчанию — «как в прошлый раз», а НЕ верх диапазона.
   *
   * Верх диапазона выглядел удобнее, но это ловушка: по double progression
   * (раздел 5.4) вес растёт, когда во ВСЕХ подходах закрыта верхняя граница
   * при RPE <= 8. Подставляя верх, приложение заявляло его за человека — и
   * тот, кто тапает не глядя, каждую тренировку получал прибавку, которой
   * не заработал. Дальше эта ложь уезжает в e1RM, прогноз и назначения.
   *
   * Порядок: факт этой тренировки -> факт прошлой сессии -> низ диапазона.
   * Прибавка теперь стоит одного тапа по «+», и это честная цена.
   */
  const repsSource = lastFact?.reps ?? block?.last_session_reps ?? null
  const defaultReps = currentSet
    ? repsSource !== null &&
      repsSource >= currentSet.target_reps_min &&
      repsSource <= currentSet.target_reps_max
      ? repsSource
      : currentSet.target_reps_min
    : lastFact?.reps ?? lastPlanned?.target_reps_min ?? 1
  const defaultRpe = currentSet
    ? currentSet.target_rpe
    : lastFact && lastFact.rpe >= RPE_MIN
      ? lastFact.rpe
      : lastPlanned?.target_rpe ?? RPE_MIN

  /** Правка относится к движению на экране: с чужой правкой поля не трогаем. */
  const editingHere = editing && block && editing.exerciseId === block.exercise.id ? editing : null

  const weight = editingHere
    ? editingHere.weight
    : draft && draft.key === currentKey
      ? draft.weight
      : defaultWeight

  /**
   * Блины на штанге: всё, что сверх пустого грифа.
   *
   * Ниже нуля не опускается. Отрицательное число здесь означало бы, что план
   * легче пустого грифа, — такого не бывает (гриф и есть `min_weight`), но
   * старый черновик или план из кеша могут прийти с прежним, более тяжёлым
   * грифом, и показывать «−2.5 блина» нельзя.
   */
  const plates = barWeight === null ? weight : Math.max(0, weight - barWeight)

  /**
   * Вес, который уйдёт в `workout_set`: гриф плюс блины.
   *
   * Для штанги считается из блинов, а не берётся из `weight` напрямую, и это
   * не перестраховка. Веса в плане может не быть вовсе (калибровочная неделя,
   * раздел 5.2) — там `weight` равен нулю, и без пересборки строка итога
   * читалась бы как «20 + 0 = 0», а в базу ушёл бы подход весом 0 кг по
   * движению, где человек как минимум поднял пустой гриф.
   *
   * Ничего не «пересчитывается» в смысле принципа №1: сложение грифа с
   * блинами — это то же самое число, только набранное по частям.
   */
  const logged = barWeight === null ? weight : barWeight + plates
  const reps = editingHere
    ? editingHere.reps
    : draft && draft.key === currentKey
      ? draft.reps
      : defaultReps
  const rpe = editingHere
    ? editingHere.rpe
    : draft && draft.key === currentKey
      ? draft.rpe
      : defaultRpe

  /** Степперы пишут либо в черновик нового подхода, либо в правку записанного. */
  function setFields(next: { weight: number; reps: number; rpe: number }): void {
    if (editingHere) setEditing({ ...editingHere, ...next })
    else setDraft({ key: currentKey, ...next })
  }

  /**
   * Движение выбыло из плана: сервер уже знал о боли (`has_active_pain`) либо
   * жалоба заведена прямо здесь и сервер её принял. Второе важно не меньше
   * первого: раздел 14 обещает исключение проблемного движения, и продолжать
   * предлагать по нему подходы значит обещать одно, а делать другое.
   */
  const isExcluded = (b: ExerciseBlock): boolean =>
    b.has_active_pain || painNotes[b.exercise.id] !== undefined

  // Подходы выбывшего движения уходят и из знаменателя: записать их уже
  // нельзя, и держать их в «записано 4 из 15» — врать про недоделанное.
  const plannedTotal = blocks.reduce(
    (sum, b) =>
      sum +
      (isExcluded(b)
        ? b.sets.filter((s) => done[b.exercise.id]?.[s.set_no]).length
        : b.sets.length),
    0,
  )
  const doneTotal = blocks.reduce(
    (sum, b) => sum + b.sets.filter((s) => done[b.exercise.id]?.[s.set_no]).length,
    0,
  )

  /**
   * Сбросить то, что принадлежит КОНКРЕТНОМУ движению.
   *
   * Панель грифа и её ошибка, жалоба на боль, открытая техника. Оставить их
   * открытыми при переходе значит предложить сменить гриф жима, стоя уже на
   * тяге, показать чужую ошибку или технику прошлого движения под новым
   * названием.
   */
  function resetForExercise(): void {
    setPainFor(null)
    setPainError('')
    setBarOpen(false)
    setBarError('')
    setTechniqueOpen(false)
    setEditing(null)
    setEditError('')
  }

  function goTo(next: number): void {
    if (!blocks.length) return
    setIndex(Math.min(Math.max(next, 0), blocks.length - 1))
    resetForExercise()
  }

  /**
   * Поставить таймер отдыха (раздел 7).
   *
   * Отсчёт на экране запускается сразу, не дожидаясь ответа: сеть в зале
   * может думать полминуты, а отдых уже идёт. Ответ сервера потом уточняет
   * момент и — главное — говорит, придёт ли напоминание вообще.
   */
  async function startRest(target: ExerciseBlock): Promise<void> {
    const seconds = target.exercise.is_compound ? REST_COMPOUND_SEC : REST_ISOLATION_SEC
    buzzed.current = false
    setRestHint('')
    setNow(Date.now())
    setRest({
      deadline: Date.now() + seconds * 1000,
      seconds,
      delivery: null,
      note: '',
      failed: null,
    })
    try {
      const timer = await api.post<RestTimer>('/rest-timer', {
        seconds,
        exercise_id: target.exercise.id,
      })
      setRest({
        deadline: deadlineOf(timer),
        seconds: timer.seconds,
        delivery: timer.delivery,
        note: timer.note,
        failed: null,
      })
    } catch (e) {
      // Подход уже записан — падать из-за таймера нельзя. Но и молчать
      // нельзя: человек рассчитывает на сигнал и не смотрит на экран.
      const reason = e instanceof NetworkError ? 'offline' : 'server'
      setRest((prev) => (prev ? { ...prev, failed: reason } : prev))
    }
  }

  function skipRest(): void {
    setRest(null)
    haptic('light')
    void api.delete('/rest-timer').catch(() => {
      setRestHint(
        'Отсчёт убран с экрана, но снять напоминание не вышло — бот всё равно может написать.',
      )
    })
  }

  /**
   * Записать подход. Это и есть тот самый один тап.
   *
   * Порядок важен: сначала галочка в локальном состоянии, потом всё
   * остальное. `recordSet` кладёт подход в IndexedDB и возвращается, не
   * дожидаясь сети, но даже этого ожидания интерфейсу ждать незачем.
   * Прямой `api.post('/sets')` здесь запрещён: в зале нет связи, и подход
   * просто исчез бы.
   */
  function handleRecord(): void {
    if (!block) return
    const exercise = block.exercise.id
    // Плановый подход и сверхплановый пишутся одинаково: в `workout_set`
    // ложится факт, а не план. Вес, повторы и RPE берём из полей — там уже
    // либо умолчание от движка, либо то, что человек поправил руками.
    // Пересчёта здесь нет ни в каком виде (принцип №1).
    const setNo = currentSet ? currentSet.set_no : extraSetNo
    // Ключ записи заводится здесь, а не в очереди: по нему этот подход потом
    // находят, чтобы исправить, — в очереди, пока он не уехал, и на сервере.
    const uuid = newUuid()

    const payload: SetInput = {
      client_uuid: uuid,
      exercise_id: exercise,
      // Номер подхода — в пределах упражнения, начиная с 1.
      set_no: setNo,
      weight: logged,
      reps,
      rpe,
      session_id: session?.id ?? null,
      is_warmup: false,
      // Момент подхода ставится ЗДЕСЬ, а не на сервере. Без него сервер
      // штампует время прихода запроса, а запрос лежит в очереди до тех пор,
      // пока не появится связь: подходы из подвального зала легли бы в базу
      // временем выхода на улицу, а то и следующим днём. `workout_set` —
      // источник правды (раздел 4), из него считаются e1RM по датам (5.3) и
      // регрессия прогноза (5.5), поэтому дата обязана быть настоящей.
      performed_at: new Date().toISOString(),
    }

    setDone((prev) => ({
      ...prev,
      [exercise]: { ...prev[exercise], [setNo]: { weight: logged, reps, rpe, uuid } },
    }))
    setDraft(null)
    setSaveError('')
    haptic('medium')
    void startRest(block)

    void recordSet(payload).catch((e: unknown) => {
      // Хранилище недоступно (приватный режим, кончилось место). Галочка уже
      // стоит, и делать вид, что всё хорошо, нельзя: подход потерян.
      setSaveError(
        `Подход ${setNo} не сохранился на устройстве: ${errorText(e, 'ошибка хранилища')}. Запиши его отдельно.`,
      )
    })
  }

  async function submitPain(exercise: number): Promise<void> {
    setPainSending(true)
    setPainError('')
    try {
      // Ответ содержит поля PainFlag плюс note_to_user — единственный текст
      // про боль, который видит человек. Своего добавлять нельзя: приложение
      // не медицинский сервис (раздел 14).
      const flag = await api.post<PainFlag & { note_to_user: string }>('/pain-flags', {
        exercise_id: exercise,
        severity,
      })
      haptic('heavy')
      setPainNotes((prev) => ({ ...prev, [exercise]: flag.note_to_user }))
      setPainFor(null)
    } catch (e) {
      setPainError(
        e instanceof NetworkError
          ? 'Нет связи — жалоба не отправлена. Повтори, когда появится сеть.'
          : errorText(e, 'Не удалось отправить жалобу'),
      )
    } finally {
      setPainSending(false)
    }
  }

  async function finish(): Promise<void> {
    if (!sessionId) return
    setFinishing(true)
    setFinishError('')
    try {
      await api.post<Session>(`/sessions/${sessionId}/complete`)
      haptic('heavy')
      navigate('/')
    } catch (e) {
      // Отсутствие связи и отказ сервера — разные истории, и путать их нельзя:
      // первое пройдёт само, второе требует что-то сделать.
      setFinishError(
        e instanceof NetworkError
          ? 'Нет связи — завершить тренировку не вышло. Попробуй, когда появится сеть.'
          : errorText(e, 'Не удалось завершить тренировку'),
      )
    } finally {
      setFinishing(false)
    }
  }

  /**
   * Сменить гриф у текущего движения.
   *
   * После записи экран перезапрашивает тренировку, а не пересчитывает веса
   * сам: гриф — это минимально возможный вес движения, и от него зависит
   * округление всей сетки (продуктовый принцип №1 — цифры приходят с
   * сервера). Черновик текущего подхода сбрасывается: он держал прежнюю
   * сумму, и после смены грифа она означала бы уже другое количество блинов.
   */
  async function changeBar(kg: number): Promise<void> {
    if (!block || barBusy) return
    const exerciseId = block.exercise.id
    setBarBusy(true)
    setBarError('')
    try {
      const saved = await api.put<Session['blocks'][number]['exercise']>(
        `/exercises/${exerciseId}/bar-weight`,
        { bar_weight: kg },
      )
      setDraft(null)
      setBarOpen(false)
      haptic('light')

      // Сначала перезапрос — веса пересчитывает сервер, — и только потом
      // подстановка сохранённого грифа поверх ответа. Порядок важен: без
      // связи перезапрос отдаёт СОХРАНЁННЫЙ план из кеша, а в нём лежит
      // прежний гриф. Экран показал бы старое число без единой ошибки, и
      // блины считались бы от него.
      await state.reload()
      state.set((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              blocks: prev.blocks.map((b) =>
                b.exercise.id === exerciseId
                  ? { ...b, exercise: { ...b.exercise, bar_weight: saved.bar_weight } }
                  : b,
              ),
            },
      )
    } catch (e) {
      setBarError(
        e instanceof NetworkError
          ? 'Нет связи — гриф не сохранился. Подходы записываются как есть, но вес легче грифа сейчас не выставить.'
          : errorText(e, 'Не удалось сменить гриф'),
      )
    } finally {
      setBarBusy(false)
    }
  }

  /**
   * Дописать движение в идущую тренировку.
   *
   * Правка плана, в отличие от записи подхода, НЕ уходит в офлайн-очередь.
   * Подход — это факт, он обязан пережить подвал без связи. План — намерение,
   * и сервер после правки возвращает пересобранную сессию: у нового движения
   * появляются подходы, а вес по нему считает движок. Подделать этот ответ
   * локально значило бы посчитать за движок (принцип №1), поэтому без связи
   * экран честно говорит, что добавить не вышло.
   *
   * После добавления экран сразу переходит на новое движение: его добавляют,
   * чтобы записать по нему подход прямо сейчас, а не чтобы полюбоваться.
   */
  async function addExercise(exerciseId: number): Promise<void> {
    if (!sessionId) return
    setAddBusy(true)
    setAddError('')
    setAddFlags([])
    try {
      const updated = await api.post<Session>(`/sessions/${sessionId}/exercises`, {
        exercise_id: exerciseId,
      })
      state.set(updated)
      setAddFlags(updated.flags ?? [])
      void saveCache(`session:${sessionId}`, updated)
      const at = updated.blocks.findIndex((b) => b.exercise.id === exerciseId)
      if (at >= 0) {
        setIndex(at)
        // Не goTo(at): тот ограничивает индекс длиной списка из замыкания, то
        // есть ДО добавления, и новое движение в конце открылось бы на месте
        // прежнего последнего.
        resetForExercise()
      }
      setAdding(false)
      haptic('light')
    } catch (e) {
      setAddError(
        e instanceof NetworkError
          ? 'Нет связи — движение не добавилось. Повтори, когда появится сеть: план правит сервер, а не очередь.'
          : errorText(e, 'Не удалось добавить движение'),
      )
    } finally {
      setAddBusy(false)
    }
  }

  function startEdit(setNo: number, fact: DoneSet): void {
    if (!block || editBusy) return
    // Повторный тап по той же строке — выйти из правки.
    if (editingHere?.setNo === setNo) {
      setEditing(null)
      setEditError('')
      return
    }
    setEditError('')
    setEditing({
      exerciseId: block.exercise.id,
      setNo,
      weight: fact.weight,
      reps: fact.reps,
      rpe: fact.rpe,
    })
    haptic('light')
  }

  /**
   * Номер записи на сервере для подхода, записанного с этого экрана.
   *
   * Экран его не знает: подход уходит через очередь, и ответ сервера экрану
   * не возвращается. Ищем по `client_uuid`; по номеру подхода — только если
   * ключа нет (запись старше этого механизма).
   */
  async function serverIdOf(exercise: number, setNo: number, uuid?: string): Promise<number> {
    const rows = await api.get<WorkoutSet[]>(
      `/sets?session_id=${session?.id ?? 0}&exercise_id=${exercise}`,
    )
    const row =
      (uuid ? rows.find((r) => r.client_uuid === uuid) : undefined) ??
      rows.find((r) => !r.is_warmup && r.set_no === setNo)
    if (!row) throw new Error('Подход не найден на сервере. Обнови экран и попробуй ещё раз.')
    return row.id
  }

  /**
   * Сохранить правку записанного подхода.
   *
   * Подход живёт в одном из двух мест, и правится там, где лежит. Пока он в
   * очереди — правится запись очереди, связь для этого не нужна: в подвале без
   * сети ошибку в только что записанном подходе исправить можно. Уже на
   * сервере — `PATCH /sets/{id}`, и тут связь нужна, как и в «Истории».
   * Уходят только изменённые поля: непереданный RPE сервер не трогает.
   */
  async function saveEdit(): Promise<void> {
    if (!editingHere || editBusy) return
    const { exerciseId: exercise, setNo } = editingHere
    const fact = done[exercise]?.[setNo]
    if (!fact) {
      setEditing(null)
      return
    }

    const changes: SetChanges = {}
    if (editingHere.weight !== fact.weight) changes.weight = editingHere.weight
    if (editingHere.reps !== fact.reps) changes.reps = editingHere.reps
    if (editingHere.rpe !== fact.rpe) changes.rpe = editingHere.rpe
    if (Object.keys(changes).length === 0) {
      setEditing(null)
      return
    }

    setEditBusy(true)
    setEditError('')
    try {
      const inQueue = fact.uuid ? await amendQueued(fact.uuid, changes) : false
      let id = fact.id
      if (!inQueue) {
        id = id ?? (await serverIdOf(exercise, setNo, fact.uuid))
        await api.patch<WorkoutSet>(`/sets/${id}`, changes)
      }
      const saved: DoneSet = {
        ...fact,
        weight: editingHere.weight,
        reps: editingHere.reps,
        rpe: editingHere.rpe,
        id,
      }
      setDone((prev) => ({ ...prev, [exercise]: { ...prev[exercise], [setNo]: saved } }))
      setEditing(null)
      haptic('light')
    } catch (e) {
      setEditError(
        e instanceof NetworkError
          ? 'Нет связи — правка не ушла. Этот подход уже на сервере: исправить его можно, когда появится сеть.'
          : errorText(e, 'Не удалось исправить подход'),
      )
    } finally {
      setEditBusy(false)
    }
  }

  /** Удалить подход, записанный по ошибке: из очереди или с сервера. */
  async function removeEdited(): Promise<void> {
    if (!editingHere || editBusy) return
    const { exerciseId: exercise, setNo } = editingHere
    const fact = done[exercise]?.[setNo]
    if (!fact) {
      setEditing(null)
      return
    }

    setEditBusy(true)
    setEditError('')
    try {
      const inQueue = fact.uuid ? await removeQueued(fact.uuid) : false
      if (!inQueue) {
        const id = fact.id ?? (await serverIdOf(exercise, setNo, fact.uuid))
        await api.delete<void>(`/sets/${id}`)
      }
      setDone((prev) => {
        const rest = { ...prev[exercise] }
        delete rest[setNo]
        return { ...prev, [exercise]: rest }
      })
      setEditing(null)
      haptic('medium')
    } catch (e) {
      setEditError(
        e instanceof NetworkError
          ? 'Нет связи — подход не удалён. Он уже на сервере: удалить его можно, когда появится сеть.'
          : errorText(e, 'Не удалось удалить подход'),
      )
    } finally {
      setEditBusy(false)
    }
  }

  /**
   * Строка записанного подхода — одна и та же в обеих ветках карточки.
   *
   * Сверхплановый помечен явно: он полноправный факт и прячать его нельзя, но
   * человек должен видеть, что этот подход назначил не движок (принцип №3).
   */
  function doneRow(setNo: number, fact: DoneSet, extra: boolean, editable: boolean) {
    const key = `${extra ? 'x' : 'p'}-${setNo}`
    const edited = editingHere?.setNo === setNo
    const cls = `wk-set done${extra ? ' extra' : ''}${edited ? ' editing' : ''}`
    const cells = (
      <>
        <span className="wk-set-no tnum">{setNo}</span>
        <span className="tnum">
          {weightLabel(fact.weight, ownWeight)} × {fact.reps}
          {extra && <span className="wk-over">сверх плана</span>}
        </span>
        <span className="faint tnum">RPE {fact.rpe.toFixed(1)}</span>
        <span className="wk-set-mark">
          <IconCheck size={14} />
        </span>
      </>
    )
    // У выбывшего движения полей на экране нет — править нечем, и строка
    // остаётся просто строкой. Такой подход правится в «Истории».
    if (!editable) {
      return (
        <div key={key} className={cls}>
          {cells}
        </div>
      )
    }
    return (
      <button
        key={key}
        type="button"
        className={cls}
        aria-pressed={edited}
        aria-label={`Исправить подход ${setNo}: ${weightLabel(fact.weight, ownWeight)} × ${fact.reps}`}
        onClick={() => startEdit(setNo, fact)}
      >
        {cells}
      </button>
    )
  }

  /**
   * Кнопка завершения — одна логика на оба места, где она появляется.
   *
   * Незаписанные подходы уйти не запрещают: человек вправе бросить тренировку
   * на середине, и блокировка тут была бы наглостью. Но второе нажатие
   * спрашиваем — промахнуться по кнопке потной рукой слишком легко.
   */
  function finishButton(className: string) {
    const label = finishing ? 'Завершаю…' : 'Завершить тренировку'
    if (doneTotal >= plannedTotal) {
      return (
        <button className={className} disabled={finishing} onClick={() => void finish()}>
          {label}
        </button>
      )
    }
    return (
      <ConfirmButton
        className={className}
        confirmLabel={`Точно? Записано ${doneTotal} из ${plannedTotal}`}
        onConfirm={() => void finish()}
      >
        {label}
      </ConfirmButton>
    )
  }

  // --- Загрузка и ошибка --------------------------------------------------

  if (!session) {
    return (
      <div className="screen wk-screen">
        {state.loading ? (
          <Loader label="Открываю тренировку…" />
        ) : (
          <>
            <ErrorBox
              error={state.error ?? new Error('Тренировка не найдена')}
              onRetry={() => void state.reload()}
            />
            <p className="faint">
              План подходов приходит с сервера, поэтому экран тренировки нужно открыть при
              связи хотя бы раз. Уже записанные подходы при этом не теряются: они лежат на
              устройстве и уедут сами.
            </p>
            <button className="btn wide ghost" onClick={() => navigate('/')}>
              К сегодняшнему дню
            </button>
          </>
        )}
      </div>
    )
  }

  // --- Пусто --------------------------------------------------------------

  if (!block) {
    return (
      <div className="screen wk-screen">
        <header className="wk-head">
          <button className="btn round wk-exit" onClick={() => navigate('/')} aria-label="Выйти">
            <IconChevronLeft size={22} />
          </button>
          <div className="wk-head-title">
            <div className="wk-label">{session.label}</div>
          </div>
          <button className="btn ghost wk-edit" onClick={() => navigate(`/plan/${session.id}`)}>
            Править
          </button>
        </header>
        <Empty
          title="В этой тренировке нет упражнений"
          hint="Похоже, программа ещё не собрана. Вернись на «Сегодня» и пересобери её — движок подставит движения и веса. Или добавь упражнения сам кнопкой «Править»."
        />
        <button className="btn wide" onClick={() => navigate('/')}>
          К сегодняшнему дню
        </button>
      </div>
    )
  }

  // --- Тренировка ---------------------------------------------------------

  const exerciseId = block.exercise.id
  const painNote = painNotes[exerciseId]
  const excluded = isExcluded(block)
  const isLast = safeIndex >= blocks.length - 1
  const allSetsDone = !currentSet
  // Записанное показываем всегда — в том числе по движению, которое только
  // что выбыло из плана: человек должен видеть, что его работа не потерялась.
  const recorded = block.sets
    .map((set) => ({ set, fact: doneHere?.[set.set_no] }))
    .filter((row): row is { set: PlannedSet; fact: DoneSet } => row.fact !== undefined)

  // Подходы, которых в плане нет: сделанные сверх плана здесь же и те, что
  // остались от прошлой правки состава. Показываем их отдельным хвостом —
  // иначе записанная работа просто исчезла бы с экрана.
  const plannedNumbers = new Set(block.sets.map((s) => s.set_no))
  const extraRecorded: Array<{ setNo: number; fact: DoneSet }> = []
  for (const [key, fact] of Object.entries(doneHere ?? {})) {
    const setNo = Number(key)
    if (!fact || plannedNumbers.has(setNo)) continue
    extraRecorded.push({ setNo, fact })
  }
  extraRecorded.sort((a, b) => a.setNo - b.setNo)

  const lastTime =
    block.last_session_weight !== null
      ? `в прошлый раз: ${weightLabel(block.last_session_weight, ownWeight)} × ${block.last_session_reps ?? '?'}`
      : 'в прошлый раз этого движения не было'

  return (
    <div className="screen wk-screen">
      {cachedAt !== null && (
        // План несвежий — говорим об этом прямо. Показать сохранённый план
        // как актуальный значит соврать: движок мог за это время пересчитать
        // веса (продуктовый принцип №3). Записывать подходы это не мешает —
        // они уедут из очереди, когда появится связь.
        <div className="flag warning" style={{ marginBottom: 12 }}>
          Нет связи. План сохранён {formatStamp(cachedAt)} — веса могли обновиться.
          Записанные подходы не потеряются.
        </div>
      )}
      <header className="wk-head">
        <button
          className="btn round wk-exit"
          onClick={() => navigate('/')}
          aria-label="Выйти из тренировки"
        >
          <IconChevronLeft size={22} />
        </button>
        <div className="wk-head-title">
          <div className="wk-label">{session.label}</div>
          {/* Одна строка, без переноса: шапка не должна расти и отнимать
              высоту у подходов. */}
          <div className="faint tnum">
            Упражнение {safeIndex + 1}/{blocks.length} · записано {doneTotal}/{plannedTotal}
          </div>
        </div>
        {/* Стойка занята, гантелей нужного веса нет — движение меняют прямо
            посреди тренировки, а не выходят из неё и не бросают подход. */}
        <button
          className="btn ghost wk-edit"
          onClick={() => navigate(`/plan/${session.id}`)}
          aria-label="Править состав тренировки"
        >
          Править
        </button>
      </header>

      {/* Прогресс полоской, а не списком: видно, где я и сколько осталось, и
          при этом высота остаётся карточке упражнения. */}
      <div className="wk-progress">
        {/* Пометки недели стоят в строке прогресса, а не в шапке: там они
            сжимали название тренировки до трёх букв. */}
        {session.is_deload && <span className="pill warn">разгрузка</span>}
        {session.is_calibration && <span className="pill accent">калибровка</span>}
        {blocks.map((b, i) => {
          const filled = b.sets.length > 0 && b.sets.every((s) => done[b.exercise.id]?.[s.set_no])
          const cls = isExcluded(b) ? 'pain' : i === safeIndex ? 'now' : filled ? 'done' : ''
          return <span key={b.exercise.id} className={`wk-seg ${cls}`} />
        })}
      </div>

      {saveError && <div className="flag critical">{saveError}</div>}

      <section className="card wk-card">
        {/* Миниатюра стоит на месте прежней «?» и открывает ту же подсказку, но
            по картинке видно, что за снаряд, ещё до тапа. От кнопки записи её
            отделяют подходы и причина веса — промахнуться между ними нельзя.
            У личных движений миниатюры нет: технику за человека приложение не
            сочиняет. */}
        <ExerciseHead
          exercise={block.exercise}
          open={techniqueOpen}
          onToggle={() => setTechniqueOpen((open) => !open)}
          title={
            <h2>
              {block.exercise.name}
              <GoalMark block={block} />
            </h2>
          }
          sub={excluded ? undefined : <div className="faint">{lastTime}</div>}
          aside={
            excluded ? undefined : (
              <span className="wk-weight tnum">
                {weightLabel(setWeight(currentSet), ownWeight)}
              </span>
            )
          }
        />

        {excluded ? (
          <>
            {/* Текст исключения даёт сервер. Своих слов про боль здесь нет и
                быть не может (раздел 14). Для жалобы, заведённой только что,
                этот текст — `note_to_user` ниже: `reason` в блоке пока ещё
                объясняет вес, а не исключение. */}
            {block.has_active_pain && (
              <div className="flag warning" style={{ marginTop: 10 }}>
                {block.reason}
              </div>
            )}
            <div className="faint" style={{ marginTop: 10 }}>
              {recorded.length + extraRecorded.length
                ? 'Оставшиеся подходы по этому движению не показаны.'
                : 'Подходы по этому движению не показаны.'}
            </div>
            {recorded.length + extraRecorded.length > 0 && (
              <div className="wk-sets">
                {recorded.map(({ set, fact }) => doneRow(set.set_no, fact, false, false))}
                {extraRecorded.map(({ setNo, fact }) => doneRow(setNo, fact, true, false))}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Объяснение движка, почему сегодня именно такой вес. «В прошлый
                раз» стоит выше — во второй строке шапки, рядом с весом. */}
            <p className="wk-reason faint">{block.reason}</p>

            <div className="wk-sets">
              {block.sets.map((s) => {
                const fact = doneHere?.[s.set_no]
                if (fact) return doneRow(s.set_no, fact, false, true)
                // Пока правится записанный подход, «текущим» на экране является
                // он: две обведённые строки спорили бы, к какой относятся поля.
                const isCurrent = !editingHere && currentSet?.set_no === s.set_no
                return (
                  <div key={s.set_no} className={`wk-set${isCurrent ? ' now' : ''}`}>
                    <span className="wk-set-no tnum">{s.set_no}</span>
                    <span className="tnum">
                      {/* «+» после диапазона — это принятая в 5/3/1 и GZCLP
                          запись подхода на максимум: столько-то и сколько
                          сможешь сверху. Плюс приходит от сервера (`amrap`),
                          клиент его не выдумывает. */}
                      {`${weightLabel(setWeight(s), ownWeight)} × ${s.target_reps_min}–${s.target_reps_max}${s.amrap ? '+' : ''}`}
                    </span>
                    <span className="faint tnum">RPE {s.target_rpe.toFixed(1)}</span>
                    <span className="wk-set-mark" />
                  </div>
                )
              })}
              {extraRecorded.map(({ setNo, fact }) => doneRow(setNo, fact, true, true))}
            </div>
            {recorded.length + extraRecorded.length > 0 && (
              <div className="faint wk-sets-hint">
                Тап по записанному подходу — исправить или удалить.
              </div>
            )}
          </>
        )}

        {painNote && (
          <div className="flag warning" style={{ marginTop: 10, marginBottom: 0 }}>
            {painNote}
          </div>
        )}
      </section>

      {!excluded && (
        <>
          {/* Пометки программы. Без них GZCLP и 5/3/1 теряют смысл: там
              последний подход на максимум и решает, двигать ли вес дальше,
              а «один рабочий подход» — это не сокращённая версия плана. */}
          {/* В режиме правки поля принадлежат УЖЕ записанному подходу, и
              пометки про следующий плановый были бы не к месту. */}
          {editingHere && (
            <div className="wk-amrap">
              Правка подхода {editingHere.setNo} — поправь цифры и сохрани
            </div>
          )}
          {!editingHere && currentSet?.amrap && (
            <div className="wk-amrap">Последний подход — на максимум повторений</div>
          )}
          {!editingHere && currentSet?.note ? (
            <div className="wk-plan-note">{currentSet.note}</div>
          ) : null}
          {!editingHere && !currentSet && (
            <div className="wk-plan-note">
              {block.sets.length
                ? `Плановые подходы записаны. Следующий уйдёт номером ${extraSetNo} — сверх плана.`
                : `В плане подходов нет. Записанный уйдёт номером ${extraSetNo} — сверх плана.`}
            </div>
          )}
          {/* Веса в плане нет — это калибровка (5.2): истории по движению нет,
              и движок честно не назначает вес. Причину он объяснил выше в
              `reason`; здесь сказано только, что делать с полем. Промолчать
              нельзя: степпер стоит на нуле, и один тап по кнопке записал бы в
              `workout_set` подход с весом 0, из которого потом и считаются
              стартовые рабочие веса. */}
          {!editingHere && currentSet && planWeight === null && !ownWeight && (
            <div className="wk-plan-note">
              Вес на сегодня не назначен.{' '}
              {barWeight !== null
                ? 'Набери блины, с которыми сделал подход'
                : 'Поставь тот, с которым сделал подход'}
              , — из этих цифр движок посчитает рабочие веса.
            </div>
          )}

          {/* Три поля, и все три предзаполнены: «сделал как написано» —
              по-прежнему один тап по большой кнопке.

              Вес правится наравне с повторами (см. шапку файла): движок
              назначает план, а сюда пишется факт. Клавиатуры нет ни у одного
              поля — в вебвью Telegram на iOS она перекрывает пол-экрана
              (раздел 13), и между подходами это дороже любого удобства. */}
          <div className="wk-steppers">
            <div className="wk-weight-field">
              {/* Штанга: человек считает блины, а не сумму.
                  Складывать 20 + 60 в уме на каждом подходе — ровно та
                  работа, которую приложение обязано делать за него. В
                  `workout_set` при этом уходит поднятый вес ЦЕЛИКОМ: e1RM,
                  прогноз и автозагрузка по RPE считаются из него, и блины
                  без грифа были бы для движка другим человеком. */}
              {barWeight !== null ? (
                <>
                  {barOpen && (
                    <div className="wk-bar">
                      <span className="faint">Гриф</span>
                      {BAR_CHOICES.map((kg) => (
                        <button
                          key={kg}
                          type="button"
                          className={kg === barWeight ? 'chip on' : 'chip'}
                          disabled={barBusy}
                          onClick={() => void changeBar(kg)}
                        >
                          {kg} кг
                        </button>
                      ))}
                      <button className="btn ghost" onClick={() => setBarOpen(false)}>
                        Отмена
                      </button>
                    </div>
                  )}
                  {barError && <div className="se-fail">{barError}</div>}
                  <Stepper
                    label="Блины"
                    value={plates}
                    step={WEIGHT_STEP_KG}
                    min={0}
                    // Потолок общий с обычным полем: он про ПОЛНЫЙ вес на
                    // штанге, а не про блины отдельно.
                    max={WEIGHT_MAX_KG - barWeight}
                    fineStep={WEIGHT_FINE_STEP_KG}
                    decimals={2}
                    unit="кг"
                    onChange={(next) => setFields({ weight: barWeight + next, reps, rpe })}
                  />
                  {/* Итог и смена грифа — одной строкой под степпером. Отдельной
                      строкой НАД ним «Гриф 20 кг — сменить» стоила сорока
                      пикселей высоты ради кнопки, которую жмут раз в месяц. */}
                  <div className="wk-total tnum">
                    {/* Без слова «блины»: оно стоит подписью над полем, а с
                        дробным весом строка с ним не помещалась в одну. */}
                    <span>
                      гриф {formatWeight(barWeight)} + {formatWeight(plates)} ={' '}
                      <b>{formatWeight(logged)} кг</b>
                    </span>
                    {/* В правке гриф не меняют: он относится к движению целиком,
                        а не к одному записанному подходу. */}
                    {!barOpen && !editingHere && (
                      <button className="btn ghost wk-bar-toggle" onClick={() => setBarOpen(true)}>
                        Сменить гриф
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <Stepper
                  label="Вес"
                  value={weight}
                  step={WEIGHT_STEP_KG}
                  min={0}
                  max={WEIGHT_MAX_KG}
                  fineStep={WEIGHT_FINE_STEP_KG}
                  decimals={2}
                  unit="кг"
                  onChange={(next) => setFields({ weight: next, reps, rpe })}
                />
              )}
            </div>
            <Stepper
              label={!editingHere && currentSet?.amrap ? 'Повторы (макс)' : 'Повторы'}
              value={reps}
              min={1}
              // В правке потолок широкий: записанные «на максимум» 60 повторов
              // не должны срезаться до 50 одним касанием степпера.
              max={editingHere || currentSet?.amrap ? REPS_MAX_AMRAP : REPS_MAX}
              onChange={(next) => setFields({ weight, reps: next, rpe })}
            />
            <Stepper
              label="RPE"
              value={rpe}
              step={0.5}
              min={RPE_MIN}
              max={10}
              decimals={1}
              onChange={(next) => setFields({ weight, reps, rpe: next })}
            />
          </div>

          {editingHere && (
            <>
              {editError && <div className="flag critical">{editError}</div>}
              {/* Удаление — под подтверждением и в потоке, а не в доке: рядом
                  с «Сохранить» под большим пальцем ему не место. */}
              <ConfirmButton
                className="btn danger wide wk-edit-delete"
                confirmLabel={`Точно удалить подход ${editingHere.setNo}?`}
                onConfirm={() => void removeEdited()}
              >
                Удалить подход {editingHere.setNo}
              </ConfirmButton>
            </>
          )}
        </>
      )}

      {restHint && <div className="faint wk-note">{restHint}</div>}

      {/* План упражнения закрыт — но это не значит, что человек закончил. Он
          мог сделать лишний подход, и молча уводить его дальше значит терять
          факт. Поэтому кнопок две: «Ещё подход» стоит здесь, в потоке, а
          обычный путь — «Следующее упражнение» — в доке под большим пальцем. */}
      {!excluded && allSetsDone && !editingHere && (
        <button className="btn big wide wk-extra" onClick={handleRecord}>
          Ещё подход
        </button>
      )}

      {/* Док: отсчёт отдыха и главная кнопка. Он прилипает к низу экрана, и
          это не украшение. Таймер появляется НАД кнопкой записи и на
          невысоком экране (вебвью Telegram, iPhone с «чёлкой») сталкивал её
          за нижний край: после каждого подхода приходилось листать, чтобы
          записать следующий. В доке кнопка остаётся под пальцем всегда, а
          степперы при нехватке высоты уезжают под него — их и так трогают
          реже, чем кнопку. */}
      <div className="wk-dock">
        {rest && (
          <div className={`wk-timer${left === 0 ? ' over' : ''}`}>
            <RestRing left={left} total={rest.seconds} />
            <div className="wk-timer-text">
              <div className="wk-clock">{left === 0 ? 'Отдых окончен' : clock(left)}</div>
              <div className="faint">
                {rest.failed === 'offline'
                  ? 'Нет связи — напоминание не поставилось, отсчёт только на этом экране'
                  : rest.failed === 'server'
                    ? 'Сервер не принял таймер — напоминание не придёт, отсчёт только на экране'
                    : rest.delivery === 'telegram'
                      ? 'Напоминание пришлём в Telegram'
                      : rest.delivery === 'none'
                        ? rest.note
                        : 'Ставлю напоминание…'}
              </div>
            </div>
            <button className="btn ghost" onClick={skipRest}>
              {left === 0 ? 'Убрать' : 'Пропустить'}
            </button>
          </div>
        )}

        {/* Движение выбыло из-за боли — подходов по нему не будет (раздел 14), и
            «ещё один» тут предлагать нельзя: остаётся только путь дальше. Тот же
            путь — главный, когда план упражнения закрыт. */}
        {editingHere ? (
          // Правка: главная кнопка дока сохраняет её. Записать новый подход в
          // это время нельзя — поля заняты чужими цифрами.
          <div className="wk-edit-actions">
            <button
              className="btn big"
              disabled={editBusy}
              onClick={() => {
                setEditing(null)
                setEditError('')
              }}
            >
              Отмена
            </button>
            <button className="btn primary big" disabled={editBusy} onClick={() => void saveEdit()}>
              {editBusy ? 'Сохраняю…' : `Сохранить подход ${editingHere.setNo}`}
            </button>
          </div>
        ) : excluded || allSetsDone ? (
          isLast ? (
            finishButton('btn primary big wide')
          ) : (
            <button className="btn primary big wide" onClick={() => goTo(safeIndex + 1)}>
              Следующее упражнение
              <IconChevronRight size={20} />
            </button>
          )
        ) : (
          <button className="btn primary big wide" onClick={handleRecord}>
            Записать подход {currentSet.set_no} из {block.sets.length}
          </button>
        )}
      </div>

      {painFor === exerciseId ? (
        <div className="card tight">
          {/* Сила боли 1–5 — это вход для движка: он исключит движение из
              плана. Ни диагноза, ни вопросов о симптомах здесь нет. */}
          <Stepper
            label="Насколько больно (1–5)"
            value={severity}
            min={1}
            max={5}
            onChange={setSeverity}
          />
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" onClick={() => setPainFor(null)}>
              Отмена
            </button>
            <button
              className="btn danger"
              style={{ flex: 1 }}
              disabled={painSending}
              onClick={() => void submitPain(exerciseId)}
            >
              {painSending ? 'Отправляю…' : 'Отметить боль'}
            </button>
          </div>
          {painError && (
            <div className="faint" style={{ color: 'var(--danger)', marginTop: 8 }}>
              {painError}
            </div>
          )}
        </div>
      ) : (
        <div className="wk-actions">
          <button
            className="btn"
            disabled={safeIndex === 0}
            onClick={() => goTo(safeIndex - 1)}
            aria-label="Предыдущее упражнение"
          >
            <IconChevronLeft size={22} />
          </button>
          <button className="btn ghost" onClick={() => setPainFor(exerciseId)}>
            Больно
          </button>
          <button
            className="btn"
            disabled={isLast}
            onClick={() => goTo(safeIndex + 1)}
            aria-label="Следующее упражнение"
          >
            <IconChevronRight size={22} />
          </button>
        </div>
      )}

      {/* Дописать движение прямо в тренировке.
          
          Раньше для этого приходилось уходить на экран правки: кнопка
          «Править» в шапке — это переход, а человек стоит у кроссовера с
          телефоном в одной руке. Сделал сгибания на предплечья, которых в
          плане нет, — записать их надо здесь, иначе работа просто не попадёт
          ни в историю, ни в объём.

          Кнопка приглушённая и стоит НИЖЕ основной: между подходами главная
          цель — «Записать подход», и перебивать её нельзя. */}
      {/* Сервер добавку принял, но предупредил — например, что грудь за неделю
          вышла выше коридора. Панель уже закрылась, поэтому текст стоит здесь,
          на месте кнопки, куда человек и смотрит. */}
      {!adding && addFlags.length > 0 && <Flags flags={addFlags} />}
      {adding ? (
        <div className="card tight wk-add">
          <div className="row between" style={{ marginBottom: 8 }}>
            <b>Добавить упражнение</b>
            <button className="btn ghost" onClick={() => setAdding(false)}>
              Отмена
            </button>
          </div>
          {addError && <div className="se-fail">{addError}</div>}
          <ExercisePicker
            catalog={catalog}
            taken={new Set(blocks.map((b) => b.exercise.id))}
            disabled={addBusy}
            pickLabel="Добавить в тренировку"
            onPick={(exercise) => void addExercise(exercise.id)}
          />
          <div className="faint" style={{ marginTop: 10 }}>
            Движение встанет в конец тренировки. Вес движок назначит по твоей
            истории — по новому движению её пока нет, и цифру он не выдумает.
          </div>
        </div>
      ) : (
        <button
          className="btn wide ghost wk-add"
          onClick={() => {
            setAddError('')
            setAddFlags([])
            setAdding(true)
          }}
        >
          <IconPlus size={18} />
          Добавить упражнение
        </button>
      )}

      {/* Завершить можно с любого упражнения, а не только с последнего:
          тренировку бросают там, где кончились силы. Дублируем кнопку только
          там, где большая кнопка выше не делает то же самое. */}
      {!(isLast && (allSetsDone || excluded)) && finishButton('btn wide ghost')}

      {finishError && (
        <div className="card" style={{ marginTop: 12, color: 'var(--danger)' }}>
          {finishError}
          <div className="faint" style={{ marginTop: 6 }}>
            Записанные подходы уже сохранены на устройстве и уедут на сервер сами. Можно
            выйти и завершить тренировку позже.
          </div>
        </div>
      )}
    </div>
  )
}
